import React, { useState, useEffect, Fragment } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Form,
} from "reactstrap";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import { useSelector, useDispatch } from "react-redux";
import BreadcrumbLink from "../../components/Common/BreadcrumbLink";
import i18n from "../../i18n";
import Spinners from "../../components/Common/Spinner";
import PaginationV2 from "../../components/Pagination/PaginationV2";
import { getOrgsAdmin, getPreRequestService, delPreRequestService } from "../../helpers/api_rombo_helper";
import AlertSuccess from "../../components/Alerts/AlertSuccess";
import AlertError from "../../components/Alerts/AlertError";
import { createSelector } from "reselect";
import { FilterType, Permissions, DataActions, UserType, RoutingServiceType } from "../../constants/enums";
import { orgId, handleSearchFilters, allowedHoursOptions } from "../../constants/utils";
import folderNoResults from "../../assets/images/questionmark-folder.png";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";

const PreRequestServices = (props) => {
  document.title = `${i18n.t("PreRequests")} - ${orgId}`;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [reloadList, setReloadList] = useState(true);
  const [drp_link, setDrp_link] = useState(false);
  const [findServices, setFindServices] = useState([]);
  const [listServices, setListServices] = useState({ page: 1, size: 10, pages: 1, items: [] });
  const [infoUser, setInfoUser] = useState({ permissions: [] });
  const [showNoResults, setShowNoResults] = useState(false);
  const [showAlertSuccess, setShowAlertSuccess] = useState(false);
  const [showAlertError, setShowAlertError] = useState(false);
  const [errorHeader, setErrorHeader] = useState("");
  const [errorBody, setErrorBody] = useState("");
  const [textHeader, setTextHeader] = useState('');
  const [textBody, setTextBody] = useState('');
  const [listCompanies, setListCompanies] = useState([]);

  const [selectedOriginAddress, setSelectedOriginAddress] = useState("");
  const [dateService, setDateService] = useState("");
  const [hourService, setHourService] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedServiceType, setSelectedServiceType] = useState("");

  const savedFilters = JSON.parse(localStorage.getItem("preRequestFilters")) || { data: { filters: {} } };
  
  const [fCompany, setFCompany] = useState(savedFilters.data?.filters?.partner_id || null);
  const [typeService, setTypeService] = useState(savedFilters.data?.filters?.type || null);
  const [serviceNumber, setServiceNumber] = useState(savedFilters.data?.filters?.code || null);
  const [placeService, setPlaceService] = useState(savedFilters.data?.filters?.place_id || null);

  const autoformik = useFormik({
    initialValues: {
      search: "",
      serviceNumber: "",
      type: "",
      place_id: "",
    },
    validationSchema: Yup.object({
      search: Yup.string(),
      serviceNumber: Yup.string(),
      type: Yup.string(),
      place_id: Yup.string(),
    }),
  });

  const LoginProperties = createSelector(
    (state) => state.Login,
    (Login) => ({
      token: Login.token,
      loading: Login.loading,
      data: Login.data,
    })
  );

  const { token, loading, data } = useSelector(LoginProperties);

  const isEmployee = infoUser.roles === UserType.EMPLOYEE;

  const handleSearch = async (page = 1) => {
    if (!hourService) {
      setSearchError(i18n.t("PleaseSelectHour"));
      return;
    }

    if (!selectedOriginAddress) {
      setSearchError(i18n.t("PleaseSelectAddress"));
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setHasSearched(true);
    setCurrentPage(page);

    try {
      const formattedTime = `${hourService}:00`;
  
      const params = {
        origin: selectedOriginAddress,
        time: formattedTime,
        timezone: "America/Mexico_City",
        date: dateService,
      }
  
      const response = await getPreRequestService(token, params);

      if (response && response.items) {
        let filteredResults = response.items;
        
        if (selectedServiceType) {
          filteredResults = response.items.filter(service => {
            const serviceType = getServiceType(service);
            return serviceType === selectedServiceType;
          });
        }
  
        setSearchResults(filteredResults);
        setTotalItems(filteredResults.length);
        setTotalPages(Math.ceil(filteredResults.length / pageSize));
        setListServices({
          page: page,
          size: pageSize,
          pages: Math.ceil(filteredResults.length / pageSize),
          items: filteredResults,
          total: filteredResults.length
        });
        
        setFindServices(filteredResults);
      } else {
        setSearchResults([]);
        setTotalItems(0);
        setTotalPages(0);
        setSearchError(i18n.t("NoResultsFound"));
      }
    } catch (error) {
      console.error("Error searching for pre-applications:", error);
      setSearchError(error?.response?.data?.detail || error.message || i18n.t("SearchError"));
      setSearchResults([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setIsSearching(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data?.data) {
      setInfoUser(data.data);
      
      if (!data.data.is_staff) {
        searchFilters(data.data.partner_id, "company");
      }
    }
  }, [data, loading]);

  useEffect(() => {
    if (infoUser?.is_staff) {
      navigate("/services/general", { replace: true });
    }
  }, [infoUser, navigate]);
  
  useEffect(() => {
    setFindServices(listServices.items);
  }, [listServices]);

  const searchFilters = (val, typeSearch) => {
    if (typeSearch === FilterType.TYPE) {
      setSelectedServiceType(val);
    }
    
    handleSearchFilters(
      val,
      typeSearch,
      { data: { action: DataActions.GET_PRE_REQUESTS, filters: {} } },
      () => {},
      setReloadList,
      setCurrentPage,
      "preRequestFilters",
      {
        setFCompany,
        setTypeService,
        setServiceNumber,
        setPlaceService,
      }
    );
  };

  const onGetOrgsAdmin = () => {
    let arr = [];
    getOrgsAdmin(token)
      .then((response) => {
        response.forEach((item) => {
          arr.push({ label: item.name, value: item.partner_id });
        });
        setListCompanies([...arr]);
      })
      .catch((error) => {
        console.error("Error - onGetOrgsAdmin - ", error);
      });
  };

  useEffect(() => {
    onGetOrgsAdmin();
  
    const checkNotification = () => {
      const shouldNotify = localStorage.getItem("notify") === "true";
      if (shouldNotify) {
        setShowAlertSuccess(true);
        setTextHeader(localStorage.getItem("headerText") || "");
        setTextBody(localStorage.getItem("bodyText") || "");
        localStorage.setItem("notify", "false");
      }
    };
  
    checkNotification();
  
    const handleStorageChange = (e) => {
      if (e.key === "notify") {
        checkNotification();
      }
    };
  
    window.addEventListener("storage", handleStorageChange);
  
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);  

  useEffect(() => {
    if (isLoading || findServices.length > 0) {
      setShowNoResults(false);
    }

    if (!isLoading && findServices.length === 0 && hasSearched) {
      const timer = setTimeout(() => {
        setShowNoResults(true);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [isLoading, findServices, hasSearched]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    
    try {
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(Date.UTC(year, month - 1, day));
      
      if (isNaN(date.getTime())) {
        return dateString;
      }
      
      return date.toLocaleDateString('es-MX', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      console.error("Error formateando fecha:", error, "Fecha original:", dateString);
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'pm' : 'am';
    const formattedHour = hour % 12 || 12;
    return `${formattedHour}:${minutes} ${ampm}`;
  };

  const getServiceType = (service) => {
    const originDesc = service.origin?.address?.description || "";
    const destDesc = service.destination?.address?.description || "";
    
    if (originDesc.includes("Norte 45 847") || originDesc.includes("Poniente 128 No. 550") || originDesc.includes("Calzada Vallejo No. 1849")) {
      return RoutingServiceType.DESAFORO;
    }
    
    if (destDesc.includes("Norte 45 847") || destDesc.includes("Poniente 128 No. 550") || destDesc.includes("Calzada Vallejo No. 1849")) {
      return RoutingServiceType.AFORO;
    }
    
    return RoutingServiceType.NORMAL;
  };

  const onChangeDate = (e) => {
    const selectedDate = e[0];
    if (selectedDate) {
      setDateService(`${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`);
    } else {
      setDateService("");
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    handleSearch(newPage);
  };

  const originAddressOptions = [
    { value: "Norte 45 847", label: "Norte 45 847" },
    { value: "Poniente 128 No. 550", label: "Poniente 128 No. 550" },
    { value: "Calzada Vallejo No. 1849", label: "Calzada Vallejo No. 1849" }
  ];

  const paginatedServices = searchResults.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const showFilterMessage = !hasSearched && (!selectedOriginAddress || !dateService || !hourService);

  const handleDeletePreRequest = async (serviceId) => {
    try {
      setLoading(true);
  
      const payload = {
        uuid: serviceId,
        status: "PENDING",
      };
  
      await delPreRequestService(token, payload);

      const updatedResults = searchResults.filter(
        (service) => service.id !== serviceId
      );
  
      setSearchResults(updatedResults);
      setFindServices(updatedResults);
      setTotalItems(updatedResults.length);
      setTotalPages(Math.ceil(updatedResults.length / pageSize));
      setTextHeader(i18n.t("SuccessPreRequestCanceled"));
      setTextBody(i18n.t("PreRequestCanceledSuccessfully"));
      setShowAlertSuccess(true);
  
    } catch (error) {
      console.error("Error deleting pre-request:", error);
  
      setErrorHeader(i18n.t("Error"));
      setErrorBody(
        error?.response?.data?.detail ||
        i18n.t("ErrorCancelingPreRequest")
      );
      setShowAlertError(true);
    } finally {
      setLoading(false);
    }
  };  

  return (
    <Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <BreadcrumbLink title={i18n.t("Services")} link='/services/pre-request' breadcrumbItem={i18n.t("Requests")} />                   
          <Row>
            <Card>
              <CardBody>
                <Row className="pb-2">
                  <Col xxl={9} md={7} xs={12}>
                    <CardTitle className="h4">{i18n.t("Services")}</CardTitle>
                    {!isLoading && (
                      <>
                        {(!infoUser.permissions.includes(Permissions.SERVICES_READ)) ? (
                          <span className="error-message-requests">
                            <i className="fa fa-exclamation-circle"></i>
                            {i18n.t("NoPermissionsViewServices")}
                          </span>
                        ) : (
                          searchResults.length === 0 && showNoResults && (
                            <span className="error-message-requests">
                              <i className="fa fa-exclamation-circle"></i>
                              {i18n.t("NoPreRequestsFound")}
                            </span>
                          )
                        )}
                      </>
                    )}
                  </Col>
                  <Col xxl={3} md={5} xs={12} className="text-end">
                    <Row>
                      <Col xxl={12} md={12} xs={12}>
                        {infoUser.permissions && infoUser.permissions.includes(Permissions.SERVICES_CREATE) && (
                          <div className="d-inline-flex align-items-center gap-3">
                            <Link
                              to="/services/pre-request/create"
                              className="btn btn-request"
                            >
                              {i18n.t("RequestAService")}
                            </Link>
                          </div>
                        )}
                      </Col>
                    </Row>
                  </Col>
                </Row>
                <Row className="pb-2">
                  <div className="orange-separator" />
                  <Form className="row gx-3" onSubmit={(e) => { e.preventDefault(); handleSearch(1); }}>
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <div className="form-group">
                        <Select
                          name="originAddress"
                          placeholder={i18n.t("Address")}
                          options={originAddressOptions}
                          isClearable
                          className="select2-selection"
                          value={originAddressOptions.find((item) => item.value === selectedOriginAddress) || null}
                          onChange={(e) => setSelectedOriginAddress(e?.value || "")}
                        />
                      </div>
                    </Col>
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <div className="form-group">
                        <Flatpickr
                          className="form-control d-block"
                          placeholder={i18n.t("Date")}
                          options={{
                            minDate: "today",
                            altInput: true,
                            altFormat: "d/m/Y",
                            dateFormat: "Y-m-d",
                          }}
                          value={dateService}
                          onChange={(e) => onChangeDate(e)}
                        />
                        <i className="mdi mdi-calendar-outline input-icon" />
                      </div>
                    </Col>
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <div className="form-group">
                        <Select
                          name="hour"
                          placeholder={i18n.t("Hour")}
                          options={allowedHoursOptions}
                          isClearable
                          className="select2-selection hour-select"
                          classNamePrefix="hour-select"
                          value={
                            allowedHoursOptions.find(
                              (item) => item.value === hourService
                            ) || null
                          }
                          onChange={(e) => {
                            const selected = e?.value || "";
                            setHourService(selected);
                          }}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          menuPlacement="auto"
                          styles={{
                            control: (base) => ({
                              ...base,
                              height: "38px",
                              paddingRight: "35px",
                              borderRadius: "0.25rem",
                              borderColor: "#CED4DA",
                              boxShadow: "none",
                              backgroundColor: "#FFFFFF",
                              "&:hover": { borderColor: "#CED4DA" },
                            }),
                            menu: (base) => ({
                              ...base,
                              backgroundColor: "#FFFFFF",
                              zIndex: 9999,
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            menuList: (base) => ({
                              ...base,
                              backgroundColor: "#FFFFFF",
                              maxHeight: "200px",
                              overflowY: "auto",
                            }),
                            indicatorsContainer: () => ({
                              display: "none",
                            }),
                            valueContainer: (base) => ({
                              ...base,
                              paddingLeft: "8px",
                            }),
                          }}
                        />
                        <i className="mdi mdi-clock-outline input-icon" style={{ color: "#FF5F00" }} />
                      </div>
                    </Col>
                    <Col xxl={2} md={2} xs={12} className="mb-2">
                      <button
                        type="submit"
                        className="btn btn-users d-flex align-items-center gap-2"
                        disabled={isSearching || !hourService || !selectedOriginAddress}
                      >
                        {isSearching ? (
                          <>
                            <Spinners size="sm" />
                            {i18n.t("Searching")}
                          </>
                        ) : (
                          i18n.t("Search")
                        )}
                      </button>
                    </Col>
                  </Form>
                  {searchError && (
                    <div className="alert alert-danger mt-2" role="alert">
                      <i className="fa fa-exclamation-circle me-2" />
                      {searchError}
                    </div>
                  )}
                  <div className="orange-separator" />
                </Row>
                <div className="table-responsive pt-3">
                  {showFilterMessage ? (
                    <div className="text-center py-5">
                      <div className="mb-4">
                        <i className="mdi mdi-filter-outline" style={{ fontSize: '4rem', color: '#FF5F00', opacity: 0.7 }} />
                      </div>
                      <h5 className="text-muted mb-3">{i18n.t("FilterRequiredTitle")}</h5>
                      <p className="text-muted mb-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                        {i18n.t("FilterRequiredMessage")}
                      </p>
                      <div className="d-flex justify-content-center gap-3 mb-4 animated-icons">
                        <div className="text-center icon-item">
                          <div className="icon-circle">
                            <i className="mdi mdi-map-marker-outline icon-symbol" />
                          </div>
                          <span className="text-muted small">{i18n.t("SelectAddress")}</span>
                        </div>
                        <div className="text-center icon-item">
                          <div className="icon-circle">
                            <i className="mdi mdi-calendar-outline icon-symbol" />
                          </div>
                          <span className="text-muted small">{i18n.t("SelectDate")}</span>
                        </div>
                        <div className="text-center icon-item">
                          <div className="icon-circle">
                            <i className="mdi mdi-clock-outline icon-symbol" />
                          </div>
                          <span className="text-muted small">{i18n.t("SelectTime")}</span>
                        </div>
                      </div>
                      <div className="tip-container mt-4">
                        <div className="tip-card">
                          <div className="tip-icon">
                            <i className="mdi mdi-information-outline" />
                          </div>
                          <div className="tip-content">
                            <strong>{i18n.t("Tip")}:</strong>
                            <span className="ms-1">{i18n.t("FilterTipMessage")}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : hasSearched ? (
                    <div>
                      {isSearching ? (
                        <div className="text-center py-4">
                          <Spinners />
                          <p className="mt-2">{i18n.t("LoadingResults")}</p>
                        </div>
                      ) : searchResults.length > 0 ? (
                        <>                        
                          <Table className="table table-bordered mb-0 table-request-services">
                            <thead>
                              <tr>
                                <th>{i18n.t("Type")}</th>
                                <th>{i18n.t("Employee")}</th>
                                <th>{i18n.t("Address")}</th>
                                <th>{i18n.t("Date")}</th>
                                <th>{i18n.t("Hour")}</th>
                                <th>{i18n.t("Status")}</th>
                                <th>{i18n.t("Actions")}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedServices.map((service, index) => {
                                const fullAddress = service.destination?.address?.description || i18n.t("NoAddress");
                                return (
                                  <tr key={service.id || index}>
                                    <td>{getServiceType(service)}</td>
                                    <td>{service.riders_name || i18n.t("NoEmployeeName")}</td>
                                    <td className="address-cell">
                                      <span style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                        {fullAddress}
                                      </span>
                                    </td>
                                    <td>
                                      {service.scheduled_start?.date 
                                        ? formatDate(service.scheduled_start.date)
                                        : 'N/A'
                                      }
                                    </td>
                                    <td>
                                      {service.scheduled_start?.time 
                                        ? formatTime(service.scheduled_start.time)
                                        : 'N/A'
                                      }
                                    </td>
                                    <td>
                                      <span className="bg-warning text-white rounded-2 text-center p-1 fs-10">
                                        {i18n.t("Pending")}
                                      </span>
                                    </td>
                                    <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        type="button"
                                        className="btn btn-deleted-request waves-effect waves-light mx-1"
                                        onClick={() => handleDeletePreRequest(service.id)}
                                      >
                                        <i className="mdi mdi-delete font-size-18 deleted-icon" />
                                      </button>
                                    </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </Table>
                          <PaginationV2
                            currentPage={currentPage}
                            pageSize={pageSize}
                            totalItems={totalItems}
                            onChangePage={handlePageChange}
                            isLoading={isSearching}
                          />
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <img 
                            src={folderNoResults} 
                            alt="No results" 
                            className="mb-3"
                            style={{ width: '80px', opacity: 0.5 }}
                          />
                          <p className="text-muted">{i18n.t("NoResultsFound")}</p>
                          <div className="mt-3">
                            <button 
                              className="btn btn-outline-secondary"
                              onClick={() => {
                                setSelectedOriginAddress("");
                                setDateService("");
                                setHourService("");
                                setHasSearched(false);
                              }}
                            >
                              <i className="mdi mdi-refresh me-2"></i>
                              {i18n.t("ClearFilters")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </CardBody>
            </Card>
          </Row>
          <AlertSuccess
            showAlertSuccess={showAlertSuccess}
            setShowAlertSuccess={setShowAlertSuccess}
            textHeader={textHeader}
            textBody={textBody}
          />
          <AlertError
            showAlertError={showAlertError}
            setShowAlertError={setShowAlertError}
            textHeader={errorHeader}
            textBody={errorBody}
          />
        </div>
      </div>
    </Fragment>
  );
};

export default PreRequestServices;
